import { type GraphQLTaggedNode } from 'react-relay';
import { type IEnvironment, createOperationDescriptor, getRequest } from 'relay-runtime';

/**
 * Retains a Relay operation for the lifetime of an SSR render.
 *
 * During SSR, component effects never run, so the `environment.retain(...)` that
 * `useQueryInteropRelay` normally issues to keep a query's data in the store is
 * never executed. Any data written to the Relay store by an imperative fetch
 * (`queryInterop`) or a cache-bridge write is therefore eligible for garbage
 * collection before the synchronous SSR render reads it back via
 * `environment.lookup(...)`. The read then reports `isMissingData`, and consumers
 * such as `SPAViewContext` silently fall back to their default/empty values —
 * which is what caused the company-hub route to redirect to home during SSR.
 *
 * Apollo does not have this problem: `client.query(...)` populates the normalized
 * cache and it persists without a live component. To reach parity during SSR we
 * retain the operation here. We intentionally never dispose the retention: the SSR
 * environment is discarded when the request finishes, so there is no leak. On the
 * client this is a no-op — the component's own retain effect owns the lifecycle.
 */
export function retainOperationForSSR(
	environment: IEnvironment,
	query: GraphQLTaggedNode,
	variables: Record<string, any> = {},
): void {
	// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
	if (!process.env.REACT_SSR) {
		return;
	}

	try {
		const operation = createOperationDescriptor(getRequest(query), variables);
		environment.retain(operation);
	} catch {
		// Best-effort: retention is an SSR data-availability safeguard and must
		// never fail the primary query/bridge operation.
	}
}
